import React from 'react';
import BentoCard from '../components/BentoCard';

const Donate = () => {
  
    const donationTiers = [
        { value: 5, label: "Sponsor a Pack", desc: "Provides one full Positive Pack to a student in need." },
        { value: 50, label: "Classroom Tools", desc: "Supplies fidgets and journals for a small group activity." },
        { value: 100, label: "Guided Activity", desc: "Funds a facilitator-led mental wellness activity for a group or school." },
        { value: 500, label: "School Event", desc: "Helps fund a 'Skate Day' or 'Fun Day' for an entire school." },
    ];

    return (
        <main className="max-w-7xl mx-auto p-4 lg:p-8 space-y-12">
        
        {/* SECTION 1: HERO & VALUE PROP */}
        <div className="text-center max-w-4xl mx-auto py-8">
            <h1 className="text-5xl font-heading font-bold text-primary-navy mb-6">
            Invest in a Resilient Future
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
            Your donation doesn&apos;t just buy supplies; it provides support and directly impacts lives. 
            Help us give every child the tools they need to navigate their emotions and thrive.
            </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* SECTION 2: DONATION ACTION (Left Side) */}
            <div className="lg:col-span-7 space-y-6">
                
                {/* Visual Impact Examples */}
                <BentoCard title="Your Impact" className="bg-white">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {donationTiers.map((tier) => (
                            <div key={tier.value} className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                                <span className="block text-xl font-bold text-primary-navy mb-1">${tier.value}</span>
                                <span className="block text-sm font-bold text-accent-orange uppercase tracking-wide mb-1">{tier.label}</span>
                                <span className="block text-xs text-gray-500">{tier.desc}</span>
                            </div>
                        ))}
                    </div>
                </BentoCard>

                {/* DONATION OPTIONS - Side by Side */}
                <p className="text-gray-500 font-bold text-sm uppercase tracking-wide">Choose how to donate:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                    {/* GIVEBUTTER (Primary) */}
                    <BentoCard className="bg-white flex flex-col items-center justify-center text-center min-h-[200px]">
                        <h3 className="font-bold text-primary-navy text-lg mb-2">Donate Online</h3>
                        <p className="text-xs text-gray-500 mb-4">Credit card, debit, or bank transfer</p>
                        <givebutter-widget id="gO8aYp" class="flex items-center justify-center"></givebutter-widget>
                    </BentoCard>

                    {/* PAYPAL (Secondary) */}
                    <BentoCard className="bg-white flex flex-col items-center justify-center text-center min-h-[200px]">
                        <h3 className="font-bold text-primary-navy text-lg mb-2">Donate via PayPal</h3>
                        <p className="text-xs text-gray-500 mb-4">No PayPal account required</p>
                        <form action="https://www.paypal.com/donate" method="post" target="_top">
                            <input type="hidden" name="hosted_button_id" value="FKGXD57HJ28XY" />
                            <button
                                type="submit"
                                className="text-center bg-[#0070BA] text-white text-base font-bold py-3 px-8 rounded-full hover:bg-[#005C99] shadow-md transition-transform hover:-translate-y-0.5 cursor-pointer flex items-center justify-center gap-2 border-2 border-[#005C99]"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.65h6.575c2.09 0 3.702.512 4.681 1.488.45.449.764.96.936 1.52.182.577.216 1.262.1 2.035l-.014.084v.74l.577.327c.418.225.753.483 1.01.777.345.395.57.876.666 1.431.1.575.065 1.261-.1 2.037-.192.902-.504 1.688-.928 2.332-.393.597-.892 1.088-1.483 1.46a5.817 5.817 0 0 1-1.9.802c-.684.165-1.442.247-2.259.247H12.8a.96.96 0 0 0-.948.813l-.046.26-.765 4.849-.035.188a.96.96 0 0 1-.948.813H7.076z"/>
                                </svg>
                                PayPal
                            </button>
                            <img alt="" border="0" src="https://www.paypalobjects.com/en_US/i/scr/pixel.gif" width="1" height="1" />
                        </form>
                    </BentoCard>

                </div>

                {/* Mail-In Option */}
                <BentoCard className="bg-gray-100 flex items-center gap-6">
                    <div className="text-4xl">✉️</div>
                    <div>
                        <h3 className="font-bold text-primary-navy mb-1">Prefer to send a check?</h3>
                        <p className="text-sm text-gray-600 mb-2">Please make checks payable to &quot;Positive Somebody&quot;.</p>
                        <p className="text-sm font-mono bg-white p-2 rounded border border-gray-200 inline-block">
                            405 W. Greenlawn Ave, STE 400 Lansing, MI 48910
                        </p>
                    </div>
                </BentoCard>
            </div>

            {/* SECTION 3: TRUST & INFO (Right Side) */}
            <div className="lg:col-span-5 space-y-6">
                
                {/* 501c3 Status */}
                <BentoCard className="text-center py-8 bg-gradient-to-br from-blue-900 to-primary-navy text-white">
                    <div className="w-20 h-20 mx-auto bg-white/10 rounded-full flex items-center justify-center text-4xl mb-4">
                        🛡️
                    </div>
                    <h3 className="text-2xl font-bold mb-2">501(c)(3) Nonprofit</h3>
                    <p className="text-blue-200 mb-4 px-4">
                        Positive Somebody is a registered tax-exempt organization. Your donation is tax-deductible to the fullest extent of the law.
                    </p>
                    {/* EIN ADDED HERE */}
                    <div className="inline-block bg-white/10 px-4 py-2 rounded-lg border border-white/20">
                        <p className="text-sm font-mono text-blue-100">EIN: 84-4387253</p>
                    </div>
                </BentoCard>

                {/* Corporate Matching */}
                <BentoCard className="bg-gradient-to-br from-green-50 to-white border border-green-100">
                    <h3 className="font-bold text-primary-forest text-lg mb-2">Double Your Impact</h3>
                    <p className="text-gray-600 text-sm mb-4">
                        Many employers will match your charitable contribution. Check with your HR department to see if your company offers gift matching.
                    </p>
                </BentoCard>

                {/* Contact for Large Gifts */}
                <BentoCard className="bg-white">
                    <h3 className="font-bold text-primary-navy text-lg mb-2">Sponsorships & Grants</h3>
                    <p className="text-gray-600 text-sm mb-4">
                        Interested in sponsoring an event or funding a specific school program? We would love to discuss partnership opportunities.
                    </p>
                    <a 
                        href="mailto:beapositivesomebody@gmail.com?subject=Partnership%20Inquiry%3A%20Sponsorships%20%26%20Grants&body=Hello%20Positive%20Somebody%20Team%2C%0D%0A%0D%0AI%20am%20interested%20in%20discussing%20partnership%20opportunities%20with%20your%20organization.%0D%0A%0D%0AWe%20are%20specifically%20interested%20in%20(please%20specify)%3A%0D%0A-%20Sponsoring%20an%20upcoming%20event%0D%0A-%20Funding%20a%20school%20program%0D%0A%0D%0AOrganization%20Name%3A%20%5BName%5D%0D%0A%0D%0AWe%20look%20forward%20to%20hearing%20from%20you."
                        className="text-primary-navy font-bold text-sm flex items-center gap-2 hover:gap-3 transition-all"
                        >
                        Contact our Director <span>→</span>
                    </a>
                </BentoCard>

            </div>

        </div>
        </main>
    );
};

export default Donate;